import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../utils/pricing';

// ===== Butler Services hub (EN) — full modernization (Implementer A scope) =====
// Design base: src/pages/about/overview + home (navy #1a2942 / brass #a38e65 / BTN-08 / editorial).
// Facts from working-tree EN pages + JA hub pricing. No fabricated facts. No Japanese characters.

export default function EnButlerServicesPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Butler Services' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.18em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const plans = [
    {
      order: '01', tier: 'I', name: 'Footman', href: '/en/services/footman/', img: '/images/generated/jba-fs-en-000-butler-services.webp',
      alt: 'Footman service. A butler supporting guests and parties',
      cadence: 'Spot use from 3 hours', price: yenInc(jbaDispatchExTax.footmanBase), unit: '~ (incl. tax, 3 hrs)',
      lead: 'The most accessible entry plan. Ideal for events, trials, and outings, a professional butler handles catering, guest reception, and temporary daily support—only for the hours you need. Perfect for first-time clients.',
      scenes: ['Home parties & guest reception', 'Reception & catering at business events', 'Anniversaries & intensive one-off housework'],
    },
    {
      order: '02', tier: 'II', name: 'Butler', href: '/en/services/butler/', img: '/images/generated/jba-fs-en-001-butler-services.webp',
      alt: 'Butler service. A dedicated butler supporting a full day',
      cadence: '8 hours per day, dedicated', price: yenInc(jbaDispatchExTax.butlerBase), unit: '~ (incl. tax, 8 hrs)',
      lead: 'A step beyond Footman. A full 8 hours lets a dedicated butler support your entire day—from morning preparation to evening cleanup—delivering the anticipatory care that defines authentic butler service. Ideal for affluent and VIP clients.',
      scenes: ['Important business days', 'Full-day VIP guest entertainment', 'Recovery periods & busy full days'],
    },
    {
      order: '03', tier: 'III', name: 'Second Steward', href: '/en/services/second-steward/', img: '/images/generated/jba-fs-en-002-butler-services.webp',
      alt: 'Second Steward service. The same butler visiting 20 days a month',
      cadence: '20 days per month, continuous', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '~ (incl. tax, monthly)',
      lead: 'From single visits to true continuity. One dedicated butler visits 20 days a month, deeply understanding your rhythms and preferences so needs are met before they are spoken. A monthly service for affluent families and family offices.',
      scenes: ['Life foundation for busy executives', 'Continuous care for family & elders', 'Regular management of villas & offices'],
    },
    {
      order: '04', tier: 'IV', name: 'House Steward', href: '/en/services/house-steward/', img: '/images/generated/jba-fs-025-butler-services.webp',
      alt: 'House Steward service. Three master butlers managing an estate around the clock',
      cadence: '24 hours, three-butler rotation', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: ' (incl. tax, monthly)',
      lead: 'The pinnacle of our service. Three master butlers rotate in 8-hour shifts to stand by around the clock, delivering uninterrupted, supreme service 24/7/365. For VVIPs, ultra-high-net-worth clients, and family offices, an ultimate partnership managing the entire estate.',
      scenes: ['Life management for busy executives', 'Family offices & large estates', 'Households needing 24-hour watch'],
    },
  ];

  return (
    <Layout>
      <title>Butler Services | Four Plans & Pricing | Japan Butler Association</title>
      <meta name="description" content="THE BUTLER, operated by the Japan Butler Association. From the 3-hour Footman to the 24-hour resident House Steward, discover four plans and pricing that support your life and business with professional butlers." />
      <link rel="canonical" href="https://butler-association.jp/en/butler-services" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img src="/images/generated/jba-fs-en-003-butler-services.webp" alt="THE BUTLER services. A refined butler supporting life and business" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">THE BUTLER — Services</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">Order your life and business,</span>
              <span className="block">with a butler at your side</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              THE BUTLER, operated by the Japan Butler Association, unites British discipline and dignity with distinctly Japanese courtesy. From short spot engagements to round-the-clock residence, our professionals comprehensively support your life and business. More than housekeeping, we deliver the very environment that lets you focus on work, family, and rest.
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          {/* S1 Why THE BUTLER */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Why THE BUTLER</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              Not a substitute for hands, but <span className="text-[#a38e65]">time and peace of mind</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>THE BUTLER's butlers are professionals cultivated by the Japan Butler Association, an institution with more than 130 years of lineage. Only those who complete a rigorous program and roughly three years of training—mastering not just technique but dignity, refinement, and a servant's heart—enter the inner circle of your home.</p>
              <p>The essence of our service is anticipatory care: sensing needs before they are spoken and moving as naturally as the air. Freed from logistics and chores, you can devote your time and attention to what truly matters.</p>
              <p>A two-way confidentiality agreement among association, butler, and client, together with anti-social-forces screening on both sides, upholds a compliance standard that withstands the strict due diligence of financial institutions—so you can entrust even private matters with confidence.</p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Heritage</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">130<span className="text-sm text-[#8f7a56] ml-1">+ yrs</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Training</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">~3<span className="text-sm text-[#8f7a56] ml-1">yrs</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Plans</dt>
                <dd className="font-['Playfair_Display',serif] text-[26px] text-[#1a2942]">4</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">Operated by</dt>
                <dd className="font-['Playfair_Display',serif] text-base text-[#1a2942] leading-tight pt-1.5">Japan Butler Association</dd>
              </div>
            </dl>
          </section>

          {/* S2 The Four Plans (step ladder) */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. The Four Plans</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              Choose from <span className="text-[#a38e65]">four plans</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-10">
              THE BUTLER's services form four stages defined by duration and depth of involvement. Begin with the short Footman, then move to Butler, Second Steward, or House Steward as needed. The deeper the engagement, the more thoroughly your butler understands you.
            </p>
            <div className="space-y-10">
              {plans.map((p, i) => (
                <div key={p.name} className="relative grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-10 border-t border-[#e2ddd2] pt-9">
                  <div className="flex lg:flex-col gap-5">
                    <img src={p.img} alt={p.alt} className="w-32 h-28 lg:w-full lg:h-56 object-cover border border-[#a38e65]/30" />
                    <div className="flex items-start gap-3">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-4xl leading-none">{p.order}</span>
                      <div className="pt-1">
                        <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.14em] text-[#8f7a56]">STEP {p.tier}</span>
                        <span className="jba-display text-[#1a2942] text-lg">{p.name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                      <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl">{p.name}</h3>
                      <span className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56]">{p.cadence}</span>
                    </div>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9] max-w-2xl mb-6">{p.lead}</p>
                    <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-end">
                      <ul className="space-y-2.5">
                        {p.scenes.map((s) => (
                          <li key={s} className="flex items-start gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">
                            <span className="mt-2 w-1.5 h-1.5 bg-[#a38e65] shrink-0"></span>{s}
                          </li>
                        ))}
                      </ul>
                      <div className="sm:text-right">
                        <div className="font-['Noto_Serif_JP',serif] text-[26px] text-[#1a2942] leading-none">
                          &yen;{p.price}<span className="text-sm text-[#8f7a56] ml-1">{p.unit}</span>
                        </div>
                        <a href={p.href} className={`${BTN08_ON_LIGHT} mt-4`}>View {p.name}</a>
                      </div>
                    </div>
                  </div>
                  {i < plans.length - 1 && (
                    <div className="hidden lg:block absolute left-[150px] -bottom-5 text-[#a38e65]/60 text-lg" aria-hidden="true">&darr;</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* S3 How to Choose (navy panel) */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. How to Choose</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Unsure which plan fits?
            </h2>
            <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14">
              <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.65] tracking-[0.02em] max-w-3xl">
                Choose by how many hours you need, and how deeply you want us involved. Most clients begin with a trial and move up the stages over time.
              </blockquote>
              <div className="w-11 h-px bg-[#a38e65] mt-8 mb-10"></div>
              <div className="grid sm:grid-cols-3 gap-8 lg:gap-10">
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Spot</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">First time or one-off</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">Start with <span className="text-[#d7b779]">Footman</span> from 3 hours. For a special full day, choose <span className="text-[#d7b779]">Butler</span>.</p>
                </div>
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Monthly</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">Ongoing support</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]"><span className="text-[#d7b779]">Second Steward</span>. The same butler visits 20 days a month, continuously ordering your daily life.</p>
                </div>
                <div>
                  <div className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Resident</div>
                  <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2.5">Entrust the whole estate</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]"><span className="text-[#d7b779]">House Steward</span>. A three-butler team on 24-hour residence manages every aspect of life.</p>
                </div>
              </div>
            </div>
          </section>

          {/* S4 How It Works (timeline) */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. How It Works</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Five steps to get started
            </h2>
            <ol className="relative border-l border-[#e2ddd2] ml-1 max-w-3xl">
              {[
                { n: '01', t: 'Contact us', d: 'Reach us by phone (03-6824-4086) or the inquiry form with your preferred date, location, and service content.' },
                { n: '02', t: 'Consultation & proposal', d: 'Our specialists listen closely and propose the best of the four plans, with a free estimate.' },
                { n: '03', t: 'Butler selection', d: 'We select and introduce the ideal butler with a profile. Naming a specific butler is free of charge.' },
                { n: '04', t: 'Agreement & payment', d: 'Confirm the service content and pricing, then contract. Payment by bank transfer or credit card.' },
                { n: '05', t: 'Service begins', d: 'Your butler arrives at the appointed time and delivers professional service.' },
              ].map((s) => (
                <li key={s.n} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                  <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="font-['Playfair_Display',serif] text-lg text-[#8f7a56]">{s.n}</span>
                    <span className="jba-display font-medium text-[#1a2942] text-lg">{s.t}</span>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* S5 Good to Know */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Good to Know</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              Before you begin
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {[
                ['Operation', 'A service based on commissioned work and introductions led by the Japan Butler Association (not temporary staffing dispatch).'],
                ['Service area', 'Primarily within and around Tokyo. Other regions available upon consultation.'],
                ['Transportation', 'In addition to each plan’s fee, transportation is settled at actual cost (some plans use a monthly flat rate).'],
                ['Application', 'Advance application and confirmation are required. Privacy is strictly protected under a confidentiality agreement.'],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{k}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* S6 CTA */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Get Started</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              We will propose the optimal butler service
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Even if you are still deciding which plan fits, that is perfectly fine. Our specialist staff will consult with you and propose the best plan for your life and budget. Please feel free to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
                <a href="https://thebutler.jp/" target="_blank" rel="noopener noreferrer" className={BTN08_ON_NAVY}>THE BUTLER Official Site</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
